import { AnimatePresence, motion, useDragControls } from 'motion/react';
import { PointerEvent as ReactPointerEvent, PropsWithChildren, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { twc } from 'react-twc';

import { Icon } from '~/modules/elements/akui/icon';
import useBaseUnitPx from '~/modules/hooks/use-base-unit-px';

export interface BottomSheetProps extends PropsWithChildren {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  // A sheet that drills into itself - the filter categories are the only one - comes back rather
  // than closing, so the level above is not lost on the way out.
  onBack?: () => void;
  // Sits beside the title, for a sheet whose explanation would otherwise be a line of prose under
  // a control. See `InfoReveal`.
  info?: ReactNode;
}

// How far the sheet has to be dragged down before letting go closes it rather than springing back.
// Measured in `rem` so it stays the same share of a sheet whatever the root font size is.
const DISMISS_DISTANCE_REM = 5.625;

const Backdrop = twc(motion.div)`fixed inset-0 z-[20002] bg-black/85 backdrop-blur-[0.5rem]`;

export function BottomSheet({ open, onClose, title, onBack, info, children }: BottomSheetProps) {
  const unit = useBaseUnitPx();
  // Dragging is started by hand from the grip and the title row rather than by the sheet listening
  // for itself, so a list inside the sheet still scrolls instead of dragging the sheet with it.
  const dragControls = useDragControls();

  const startDrag = (event: ReactPointerEvent) => dragControls.start(event);

  const sheet = (
    <AnimatePresence>
      {open && (
        <>
          <Backdrop
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            data-test="sheet-backdrop"
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            data-test="sheet"
            drag="y"
            dragListener={false}
            dragControls={dragControls}
            dragConstraints={{ top: 0, bottom: 0 }}
            // Locked upwards, free downwards: a sheet is dismissed by pushing it off the bottom, and
            // pulling it past the top of the screen has nothing to reveal.
            dragElastic={{ top: 0, bottom: 1 }}
            onDragEnd={(_event, drag) => {
              if (drag.offset.y > unit * DISMISS_DISTANCE_REM) onClose();
            }}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%', transition: { duration: 0.2, ease: [0.22, 0.61, 0.36, 1] } }}
            transition={{ duration: 0.26, ease: [0.22, 0.61, 0.36, 1] }}
            className="fixed right-0 bottom-0 left-0 z-[20003] flex max-h-[88%] flex-col gap-3 rounded-t-[1.625rem] bg-[#05070b] px-5 pt-3.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.14)]"
            style={{ paddingBottom: 'max(1.25rem, env(safe-area-inset-bottom))' }}>
            <div
              onPointerDown={startDrag}
              data-test="sheet-grip"
              className="flex shrink-0 cursor-grab touch-none flex-col gap-3 pb-0.5">
              <span aria-hidden className="mx-auto h-[0.3125rem] w-11 shrink-0 rounded-full bg-white/25" />
              <div className="flex shrink-0 items-center gap-3">
                {onBack && (
                  <button
                    type="button"
                    onClick={onBack}
                    data-test="sheet-back"
                    aria-label="Back"
                    className="flex size-10 shrink-0 items-center justify-center rounded-xl border-none bg-white/8 text-white">
                    <Icon icon="ic:baseline-arrow-back" size={5.5} />
                  </button>
                )}
                <span className="flex min-w-0 flex-1 items-center gap-2">
                  {title && <span className="min-w-0 truncate text-[1.375rem] font-bold">{title}</span>}
                  {info}
                </span>
                <button
                  type="button"
                  onClick={onClose}
                  data-test="sheet-close"
                  aria-label="Close"
                  className="flex size-10 shrink-0 items-center justify-center rounded-xl border-none bg-white/8 text-white transition-transform duration-100 active:scale-[0.92]">
                  <Icon icon="ic:baseline-close" size={5.5} />
                </button>
              </div>
            </div>
            <div className="flex min-h-0 flex-col overflow-y-auto overscroll-contain">{children}</div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  return createPortal(sheet, document.body);
}
